import classNames from 'classnames'
import { useEffect } from 'react'
import { createPortal } from 'react-dom'

type ConfirmDialogProps = {
  isOpen: boolean
  title: string
  message: string
  cancelLabel: string
  confirmLabel: string
  onCancel: () => void
  onConfirm: () => void
  confirmVariant?: 'danger' | 'primary'
}

const ConfirmDialog = ({
  isOpen,
  title,
  message,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
  confirmVariant = 'primary',
}: ConfirmDialogProps) => {
  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel()
    }

    document.body.classList.add('overflow-hidden')
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.classList.remove('overflow-hidden')
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen, onCancel])

  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={onCancel}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-message"
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={event => event.stopPropagation()}
      >
        <div className="border-b border-gray-100 px-6 py-5">
          <h2 id="confirm-dialog-title" className="text-lg font-bold text-imovSecondary">
            {title}
          </h2>
          <p id="confirm-dialog-message" className="mt-2 text-sm leading-relaxed text-gray-600">
            {message}
          </p>
        </div>
        <div className="flex flex-col-reverse gap-2 px-6 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-100"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={classNames(
              'rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-sm',
              confirmVariant === 'danger'
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-imovPrimary hover:bg-imovPrimaryDark'
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default ConfirmDialog
